import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';

// Configure how notifications appear when the app is foregrounded
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

export async function requestNotificationPermission() {
  if (Platform.OS === 'web') return false;
  const { status: existing } = await Notifications.getPermissionsAsync();
  if (existing === 'granted') return true;
  const { status } = await Notifications.requestPermissionsAsync();
  return status === 'granted';
}

export async function scheduleFollowUpNotification({ clientName, note, date }) {
  if (Platform.OS === 'web') return null;
  try {
    const granted = await requestNotificationPermission();
    if (!granted) return null;

    const triggerDate = new Date(date);
    triggerDate.setHours(9, 0, 0, 0); // 9am on the reminder day
    if (triggerDate <= new Date()) return null; // past — don't schedule

    const id = await Notifications.scheduleNotificationAsync({
      content: {
        title: `Follow up: ${clientName}`,
        body: note || 'Time to reach out to this client.',
        sound: true,
        data: { type: 'follow_up', clientName },
      },
      trigger: { date: triggerDate },
    });
    return id;
  } catch (e) {
    console.warn('Failed to schedule notification:', e.message);
    return null;
  }
}

export async function sendLocalNotification({ title, body, data = {} }) {
  if (Platform.OS === 'web') return;
  try {
    const granted = await requestNotificationPermission();
    if (!granted) return;
    await Notifications.scheduleNotificationAsync({
      content: { title, body, sound: true, data },
      trigger: null, // immediate
    });
  } catch (e) {
    console.warn('Failed to send notification:', e.message);
  }
}

export async function cancelNotification(id) {
  if (!id || Platform.OS === 'web') return;
  try {
    await Notifications.cancelScheduledNotificationAsync(id);
  } catch {}
}

// Check for overdue invoices and send a single bundled notification.
// Call this once on app load — it checks Supabase and fires immediately if needed.
// Uses a daily repeat trigger so subsequent checks happen automatically at 9am.
export async function checkOverdueInvoices(supabase) {
  if (Platform.OS === 'web') return;
  try {
    const granted = await requestNotificationPermission();
    if (!granted) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const today = new Date().toISOString().split('T')[0];
    const { data: overdue } = await supabase
      .from('invoices')
      .select('id, client_name, total, due_date')
      .eq('user_id', user.id)
      .in('status', ['unpaid', 'overdue'])
      .lt('due_date', today)
      .not('due_date', 'is', null);

    if (!overdue?.length) return;

    const total = overdue.reduce((s, inv) => s + (inv.total || 0), 0);
    const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total);
    const count = overdue.length;

    // Only fire once per day — check if we already sent one today
    const NOTIF_KEY = 'slate_overdue_notif_date';
    let AsyncStorage = null;
    try { AsyncStorage = require('@react-native-async-storage/async-storage').default; } catch (_) {}
    if (AsyncStorage) {
      const lastSent = await AsyncStorage.getItem(NOTIF_KEY).catch(() => null);
      if (lastSent === today) return; // Already notified today
      await AsyncStorage.setItem(NOTIF_KEY, today).catch(() => {});
    }

    await Notifications.scheduleNotificationAsync({
      content: {
        title: count === 1
          ? `Invoice overdue: ${overdue[0].client_name}`
          : `${count} invoices overdue`,
        body: count === 1
          ? `${formatted} past due — tap to view`
          : `${formatted} total past due — tap to review`,
        sound: true,
        data: { type: 'overdue_invoices' },
      },
      trigger: null, // immediate
    });
  } catch (e) {
    console.warn('checkOverdueInvoices error:', e.message);
  }
}

// Schedule a daily 9am reminder to check for overdue invoices.
// Safe to call on every launch — cancels old trigger before creating new one.
export async function scheduleDailyOverdueCheck() {
  if (Platform.OS === 'web') return;
  try {
    const granted = await requestNotificationPermission();
    if (!granted) return;
    // Cancel any existing daily overdue check
    const scheduled = await Notifications.getAllScheduledNotificationsAsync();
    for (const n of scheduled) {
      if (n.content?.data?.type === 'daily_overdue_check') {
        await Notifications.cancelScheduledNotificationAsync(n.identifier);
      }
    }
    await Notifications.scheduleNotificationAsync({
      content: {
        title: 'Slate',
        body: 'You have unpaid invoices — tap to review.',
        sound: true,
        data: { type: 'daily_overdue_check' },
      },
      trigger: { hour: 9, minute: 0, repeats: true },
    });
  } catch (e) {
    console.warn('scheduleDailyOverdueCheck error:', e.message);
  }
}
